import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { getSession } from '@/lib/api';
import { asPlan } from '@/stores/auth';

/**
 * Views are lazy except the deck browser, which is the app's front door and
 * the screen most likely to be opened with no connection.
 *
 * Every account is a real account now — there is no guest tier left to serve,
 * so the app is sign-in-gated by default. `/` doubles as that gate: signed
 * out, it shows the sign-in form itself (see HomeView); signed in, it is the
 * ordinary landing page. `/auth/verify` is the one other route that must stay
 * reachable while signed out, since the emailed magic link points at it.
 */
const PUBLIC_ROUTES = new Set(['home', 'auth-verify']);

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: () => import('@/views/HomeView.vue') },
  { path: '/arcana', name: 'decks', component: () => import('@/views/DeckSelect.vue') },
  {
    path: '/arcana/:deck',
    name: 'arcana',
    component: () => import('@/views/CardBrowser.vue'),
    props: true,
  },
  { path: '/reading', name: 'reading', component: () => import('@/views/ReadingView.vue') },
  { path: '/horoscope/:sign?', name: 'horoscope', component: () => import('@/views/HoroscopeView.vue') },
  { path: '/dreams', name: 'dreams', component: () => import('@/views/DreamView.vue') },
  {
    path: '/reading/:id',
    name: 'reading-detail',
    component: () => import('@/views/ReadingDetail.vue'),
    props: true,
  },
  // Bare `/auth` collapses into the home gate rather than duplicating it.
  { path: '/auth', redirect: (to) => ({ name: 'home', query: to.query }) },
  // The emailed magic link lands here.
  { path: '/auth/verify', name: 'auth-verify', component: () => import('@/views/AuthView.vue') },
  { path: '/account', name: 'account', component: () => import('@/views/AccountView.vue') },
  { path: '/admin', name: 'admin', component: () => import('@/views/AdminView.vue') },
  { path: '/settings', name: 'settings', component: () => import('@/views/SettingsView.vue') },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, saved) {
    if (saved) return saved;
    // Changing card within a deck should not jump the page to the top.
    if (to.name === from.name) return {};
    return { top: 0 };
  },
});

router.beforeEach((to) => {
  // Someone who is already signed in and arrives from the pricing page has no
  // sign-in to do: send them to the plan's checkout directly.
  const plan = asPlan(to.query.plan);
  if (plan && to.name === 'home' && getSession()) {
    return { name: 'account', query: { buy: plan } };
  }

  if (!PUBLIC_ROUTES.has(to.name as string) && !getSession()) {
    return { name: 'home', query: { next: to.fullPath } };
  }
  return true;
});
